import { Checkbox } from '@vibe/core';
import { AppBanner } from '@/components/ui';
import { useTranslation } from '@/i18n/useTranslation';
import wizardForm from '@/styles/wizard.module.css';
import styles from './ConsentStep.module.css';

export type ConsentChecklistKey = 'informational' | 'dataProcessing' | 'layers';

export type ConsentChecklistState = Record<ConsentChecklistKey, boolean>;

export const EMPTY_CONSENT_CHECKLIST: ConsentChecklistState = {
  informational: false,
  dataProcessing: false,
  layers: false,
};

export function isConsentChecklistComplete(checked: ConsentChecklistState): boolean {
  return Object.values(checked).every(Boolean);
}

export interface ConsentStepProps {
  checked: ConsentChecklistState;
  error?: string;
  onChange: (key: ConsentChecklistKey, value: boolean) => void;
  openFileConfirmed: boolean;
  onOpenFileConfirmChange: (value: boolean) => void;
  openFileConfirmError?: string;
  submitError?: string;
}

const CHECKLIST_KEYS: ConsentChecklistKey[] = [
  'informational',
  'dataProcessing',
  'layers',
];

export default function ConsentStep({
  checked,
  error,
  onChange,
  openFileConfirmed,
  onOpenFileConfirmChange,
  openFileConfirmError,
  submitError,
}: ConsentStepProps) {
  const t = useTranslation();

  return (
    <section className={`${wizardForm.formFields} ${styles.consent}`} data-testid="consent-step">
      <AppBanner
        variant="info"
        title={t.wizard.consent.noticeTitle}
        text={t.wizard.consent.noticeBody}
      />

      <p className={styles.checklistTitle}>{t.wizard.consent.checklistTitle}</p>
      <ul className={styles.checklist}>
        {CHECKLIST_KEYS.map((key) => (
          <li key={key} className={styles.checkboxRow}>
            <Checkbox
              className={styles.checkboxControl}
              labelClassName={styles.checkboxLabel}
              checked={checked[key]}
              onChange={(e) => onChange(key, e.target.checked)}
              label={t.wizard.consent.checklistItems[key]}
              aria-label={t.wizard.consent.checklistItems[key]}
              data-testid={`consent-check-${key}`}
            />
          </li>
        ))}
      </ul>

      {error ? (
        <AppBanner variant="danger" text={error} compact role="alert" data-testid="consent-error" />
      ) : null}

      <div className={styles.openFile} data-testid="consent-open-file">
        <h3 className={styles.openFileTitle}>{t.wizard.consent.openFileTitle}</h3>
        <Checkbox
          className={styles.checkboxControl}
          labelClassName={styles.checkboxLabel}
          checked={openFileConfirmed}
          onChange={(e) => onOpenFileConfirmChange(e.target.checked)}
          label={t.wizard.consent.openFileConfirm}
          data-testid="consent-open-file-confirm"
        />
        {openFileConfirmError ? (
          <AppBanner
            variant="danger"
            text={openFileConfirmError}
            compact
            role="alert"
            data-testid="consent-open-file-error"
          />
        ) : null}
      </div>

      {submitError ? (
        <AppBanner variant="danger" text={submitError} compact role="alert" data-testid="consent-submit-error" />
      ) : null}
    </section>
  );
}
